import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDonorAuth } from './DonorAuthContext';
import { signIn } from './provider';
import { mayAutoSignIn, recordAutoSignIn } from './autoSignIn';
import { portalPath } from '../portalPaths';

/**
 * Keeps signed-out visitors off the donor pages -- by sending them straight to
 * the hosted sign-in page.
 *
 * A donor following a link to /donor-portal lands on the Masjidal sign-in page
 * with no extra click. One already signed in to another Masjidal product does
 * not see a sign-in page at all: single sign-on hands back a code immediately.
 *
 * Two safeguards make the automatic redirect safe:
 *
 *   - It replaces the current history entry. Otherwise Back on the sign-in page
 *     would return here, which would redirect forward again: a Back-button trap.
 *   - It happens at most once per cooldown (see autoSignIn.js). Inside the
 *     cooldown this falls back to the login page and its button, which cannot
 *     loop. That is what stops a token the API keeps refusing from cycling
 *     between here and Cognito forever.
 *
 * The login page itself never redirects automatically. It is where donors land
 * after signing out and after a failed or cancelled sign-in, and in each of those
 * cases bouncing them back to Cognito would be wrong.
 *
 * This is a routing convenience, not a security boundary, and the distinction is
 * worth being explicit about because the admin app's equivalent
 * (src/protected/auth.guard.jsx) is easy to mistake for one: it gates on a
 * JS-readable cookie and reads the user's role out of localStorage, both of which
 * the user controls. Nothing here is trusted by the server. A donor who forced
 * this component to render its children would reach pages that immediately
 * receive 401s, because every API call is authorised independently from a
 * verified Cognito token.
 */
export const DonorAuthGuard = ({ children }) => {
    const { isLoading, isSignedIn, isSigningOut, configured } = useDonorAuth();
    const location = useLocation();

    const [fallBackToLogin, setFallBackToLogin] = useState(false);
    // Decided once per signed-out episode, not per render: after recording the
    // attempt, a re-render would otherwise see the cooldown and race the
    // redirect with an in-app navigation.
    const attempted = useRef(false);

    // Never while signing out: the redirect would cancel the browser's trip to
    // Cognito's /logout and the donor would be signed straight back in.
    const needsSignIn = configured && !isLoading && !isSignedIn && !isSigningOut;

    useEffect(() => {
        if (!needsSignIn || attempted.current) return;
        attempted.current = true;

        if (!mayAutoSignIn()) {
            setFallBackToLogin(true);
            return;
        }

        recordAutoSignIn();
        signIn(`${location.pathname}${location.search}`, { replace: true }).catch(() =>
            // e.g. WebCrypto unavailable outside a secure context. The login
            // page's button will surface the actual message.
            setFallBackToLogin(true),
        );
    }, [needsSignIn, location.pathname, location.search]);

    if (!configured) {
        return (
            <div className="dp-auth-shell">
                <div className="dp-auth-card">
                    <h1 className="dp-auth-title">Donor Portal unavailable</h1>
                    <p className="dp-auth-sub">
                        The donor portal is not configured for this environment. Please contact
                        support.
                    </p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="dp-auth-shell">
                <div className="dp-auth-card" role="status" aria-live="polite">
                    <p className="dp-auth-sub mb-0">Checking your session...</p>
                </div>
            </div>
        );
    }

    if (isSignedIn) {
        return children;
    }

    if (isSigningOut) {
        return (
            <div className="dp-auth-shell">
                <div className="dp-auth-card" role="status" aria-live="polite">
                    <p className="dp-auth-sub mb-0">Signing you out...</p>
                </div>
            </div>
        );
    }

    if (fallBackToLogin) {
        // Remember where they were headed so sign-in can return them there.
        return <Navigate to={portalPath('login')} replace state={{ from: location.pathname }} />;
    }

    return (
        <div className="dp-auth-shell">
            <div className="dp-auth-card" role="status" aria-live="polite">
                <p className="dp-auth-sub mb-0">Taking you to sign in...</p>
            </div>
        </div>
    );
};

export default DonorAuthGuard;
